import { Component, OnInit, inject, signal, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ParkingTypeService, ParkingTypeDefnitionDto, ParkingSpaceDto } from '../../../../api/gen';
import { JjamesButtonComponent } from 'shared-lib';
import { AddDefaultExemptionComponent, DefaultExemptionResult } from './add-default-exemption/add-default-exemption.component';
import { AddCustomExemptionComponent, CustomExemptionResult } from './add-custom-exemption/add-custom-exemption.component';
import { AddAssignedZoneTypeComponent, AssignedZoneTypeResult } from './add-assigned-zone-type/add-assigned-zone-type.component';
import { AddPoolParkingComponent, PoolParkingResult } from './add-pool-parking/add-pool-parking.component';
import { AddLongTermTypeComponent, LongTermTypeResult } from './add-long-term-type/add-long-term-type.component';
import { AddShortTermTypeComponent, ShortTermTypeResult } from './add-short-term-type/add-short-term-type.component';
import { AddEasypassTypeComponent, EasypassTypeResult } from './add-easypass-type/add-easypass-type.component';

export interface ParkingTypeAddDialogData {
  parkingSpace: ParkingSpaceDto;
  parkingOperatorId: string;
}

export type ParkingTypeCategory = 'default-exemption' | 'custom-exemption' | 'assigned-zone-parkers' | 'pool-parking' | 'long-term' | 'short-term' | 'easypass';

interface ParkingTypeOption {
  id: ParkingTypeCategory;
  name: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-parking-type-add',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    JjamesButtonComponent,
    AddDefaultExemptionComponent,
    AddCustomExemptionComponent,
    AddAssignedZoneTypeComponent,
    AddPoolParkingComponent,
    AddLongTermTypeComponent,
    AddShortTermTypeComponent,
    AddEasypassTypeComponent
  ],
  templateUrl: './parking-type-add.component.html',
  styleUrl: './parking-type-add.component.scss'
})
export class ParkingTypeAddComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<ParkingTypeAddComponent>);
  private dialogData = inject<ParkingTypeAddDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);

  @ViewChild(AddDefaultExemptionComponent) defaultExemptionComponent?: AddDefaultExemptionComponent;
  @ViewChild(AddCustomExemptionComponent) customExemptionComponent?: AddCustomExemptionComponent;
  @ViewChild(AddAssignedZoneTypeComponent) assignedZoneComponent?: AddAssignedZoneTypeComponent;
  @ViewChild(AddPoolParkingComponent) poolParkingComponent?: AddPoolParkingComponent;
  @ViewChild(AddLongTermTypeComponent) longTermComponent?: AddLongTermTypeComponent;
  @ViewChild(AddShortTermTypeComponent) shortTermComponent?: AddShortTermTypeComponent;
  @ViewChild(AddEasypassTypeComponent) easypassComponent?: AddEasypassTypeComponent;

  // Data
  parkingTypeOptions: ParkingTypeOption[] = [
    {
      id: 'default-exemption',
      name: 'Default Exemption',
      description: 'Exempt vehicles by license plate pattern',
      icon: 'verified_user'
    },
    {
      id: 'custom-exemption',
      name: 'Custom Exemption',
      description: 'Exempt specific license plates',
      icon: 'playlist_add_check'
    },
    {
      id: 'assigned-zone-parkers',
      name: 'Assigned Zone Parkers',
      description: 'Assign parkers to specific zones',
      icon: 'person_pin'
    },
    {
      id: 'pool-parking',
      name: 'Pool Parking',
      description: 'Link to an organization pool parking configuration',
      icon: 'groups'
    },
    {
      id: 'long-term',
      name: 'Long-Term',
      description: 'Long-term parkers with extended access',
      icon: 'event_repeat'
    },
    {
      id: 'short-term',
      name: 'Short-Term',
      description: 'Visitors admitted and billed per the short-term tariff',
      icon: 'schedule'
    }
    // ,
    // {
    //   id: 'easypass',
    //   name: 'Easypass',
    //   description: 'Enable Easypass for this space, then issue QR passes from the list',
    //   icon: 'qr_code'
    // }
  ];

  // State
  isLoading = signal(false);
  selectedType = signal<ParkingTypeCategory | null>(null);

  // Form
  form!: FormGroup;

  get parkingSpace(): ParkingSpaceDto {
    return this.dialogData.parkingSpace;
  }

  get parkingOperatorId(): string {
    return this.dialogData.parkingOperatorId;
  }

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      parkingTypeId: ['', Validators.required]
    });

    // Watch for type selection changes
    this.form.get('parkingTypeId')?.valueChanges.subscribe((value: ParkingTypeCategory) => {
      this.selectedType.set(value);
    });
  }

  onSubmit(): void {
    if (!this.selectedType()) {
      this.snackBar.open('Please select a parking type', 'Close', { duration: 3000 });
      return;
    }

    // Trigger submit on the appropriate child component
    switch (this.selectedType()) {
      case 'default-exemption':
        this.defaultExemptionComponent?.submit();
        break;
      case 'custom-exemption':
        this.customExemptionComponent?.submit();
        break;
      case 'assigned-zone-parkers':
        this.assignedZoneComponent?.submit();
        break;
      case 'pool-parking':
        this.poolParkingComponent?.submit();
        break;
      case 'long-term':
        this.longTermComponent?.submit();
        break;
      case 'short-term':
        this.shortTermComponent?.submit();
        break;
      case 'easypass':
        this.easypassComponent?.submit();
        break;
    }
  }

  onChildSubmitted(result: DefaultExemptionResult | CustomExemptionResult | AssignedZoneTypeResult | PoolParkingResult | LongTermTypeResult | ShortTermTypeResult | EasypassTypeResult): void {
    if (result.success) {
      this.dialogRef.close(true);
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getTypeIcon(type: ParkingTypeDefnitionDto): string {
    switch (type.type) {
      case 'Exemption':
        return 'verified_user';
      case 'AssignedZoneParkers':
        return 'person_pin';
      default:
        return 'local_parking';
    }
  }
}
